import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Box,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Tooltip,
  IconButton,
  TextField,
  Grid,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  addDoc,
  updateDoc,
  query,
  orderBy,
} from 'firebase/firestore';

const AdminCategoriasOtro = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoriaToDelete, setCategoriaToDelete] = useState(null);
  const [actionStatus, setActionStatus] = useState({
    success: false,
    error: null,
    message: '',
  });

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [categoriaForm, setCategoriaForm] = useState({
    nombre: '',
    descripcion: '',
  });

  useEffect(() => {
    fetchCategorias();
  }, []);

  const fetchCategorias = async () => {
    try {
      setLoading(true);
      const categoriasRef = collection(db, 'categorias_otro');
      const q = query(categoriasRef, orderBy('nombre'));
      const querySnapshot = await getDocs(q);

      const categoriasData = [];
      querySnapshot.forEach((doc) => {
        categoriasData.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setCategorias(categoriasData);
      setError(null);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      setError('Error al cargar las categorías. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (categoria) => {
    setCategoriaToDelete(categoria);
    setDeleteDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDeleteDialogOpen(false);
    setCategoriaToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!categoriaToDelete) return;

    try {
      setLoading(true);
      await deleteDoc(doc(db, 'categorias_otro', categoriaToDelete.id));
      setCategorias((prevCategorias) =>
        prevCategorias.filter((categoria) => categoria.id !== categoriaToDelete.id)
      );
      setActionStatus({
        success: true,
        error: null,
        message: 'Categoría eliminada correctamente.',
      });
      setTimeout(() => {
        setActionStatus({ success: false, error: null, message: '' });
      }, 3000);
      handleCloseDialog();
    } catch (error) {
      console.error('Error al eliminar categoría:', error);
      setActionStatus({
        success: false,
        error: true,
        message: 'Error al eliminar la categoría. Por favor intente nuevamente.',
      });
      setTimeout(() => {
        setActionStatus({ success: false, error: null, message: '' });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditingCategoria(null);
    setCategoriaForm({
      nombre: '',
      descripcion: '',
    });
    setFormDialogOpen(true);
  };

  const handleEditClick = (categoria) => {
    setEditingCategoria(categoria);
    setCategoriaForm({
      nombre: categoria.nombre,
      descripcion: categoria.descripcion || '',
    });
    setFormDialogOpen(true);
  };

  const handleCloseForm = () => {
    setFormDialogOpen(false);
    setEditingCategoria(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setCategoriaForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveCategoria = async () => {
    if (!categoriaForm.nombre.trim()) {
      setActionStatus({
        success: false,
        error: true,
        message: 'El nombre de la categoría es obligatorio.',
      });
      return;
    }

    try {
      setLoading(true);

      if (editingCategoria) {
        const categoriaRef = doc(db, 'categorias_otro', editingCategoria.id);
        await updateDoc(categoriaRef, {
          nombre: categoriaForm.nombre.trim(),
          descripcion: categoriaForm.descripcion.trim(),
          updatedAt: new Date(),
        });

        setCategorias((prevCategorias) =>
          prevCategorias.map((categoria) =>
            categoria.id === editingCategoria.id
              ? {
                  ...categoria,
                  nombre: categoriaForm.nombre.trim(),
                  descripcion: categoriaForm.descripcion.trim(),
                }
              : categoria
          )
        );

        setActionStatus({
          success: true,
          error: null,
          message: 'Categoría actualizada correctamente.',
        });
      } else {
        const newCategoria = {
          nombre: categoriaForm.nombre.trim(),
          descripcion: categoriaForm.descripcion.trim(),
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        const docRef = await addDoc(collection(db, 'categorias_otro'), newCategoria);

        setCategorias((prevCategorias) => [
          ...prevCategorias,
          { id: docRef.id, ...newCategoria },
        ]);

        setActionStatus({
          success: true,
          error: null,
          message: 'Categoría agregada correctamente.',
        });
      }

      setTimeout(() => {
        setActionStatus({ success: false, error: null, message: '' });
      }, 3000);

      handleCloseForm();
    } catch (error) {
      console.error('Error al guardar categoría:', error);
      setActionStatus({
        success: false,
        error: true,
        message: `Error al ${editingCategoria ? 'actualizar' : 'crear'} la categoría. Por favor intente nuevamente.`,
      });
      setTimeout(() => {
        setActionStatus({ success: false, error: null, message: '' });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={500} color="primary">
          Administración de Categorías Otro
        </Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddClick}>
          Nueva Categoría
        </Button>
      </Box>

      {actionStatus.message && (
        <Alert severity={actionStatus.error ? 'error' : 'success'} sx={{ mb: 2 }}>
          {actionStatus.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && categorias.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Nombre</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categorias.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No hay categorías registradas
                  </TableCell>
                </TableRow>
              ) : (
                categorias.map((categoria) => (
                  <TableRow key={categoria.id}>
                    <TableCell>{categoria.nombre}</TableCell>
                    <TableCell>{categoria.descripcion || '-'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton onClick={() => handleEditClick(categoria)} color="primary">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton onClick={() => handleDeleteClick(categoria)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={deleteDialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar la categoría "{categoriaToDelete?.nombre}"? Esta
            acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={formDialogOpen} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategoria ? 'Editar Categoría' : 'Nueva Categoría'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="nombre"
                label="Nombre de la Categoría"
                value={categoriaForm.nombre}
                onChange={handleFormChange}
                fullWidth
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="descripcion"
                label="Descripción"
                value={categoriaForm.descripcion}
                onChange={handleFormChange}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleSaveCategoria} color="primary" variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminCategoriasOtro;
